/**
 * Formats a number into a human-readable string using k, m, B, or T suffixes.
 *
 * @param num The number to format.
 * @param decimals The number of decimal places to include for formatted numbers (default is 1).
 * @returns The formatted string (e.g., "11k", "5.5m", "2B"), or the original number if it's not a valid number.
 */
export const formatLargeNumber = (num: number, decimals = 1) => {
  // Check if the input is a valid number
  if (typeof num !== "number" || isNaN(num)) {
    return num; // Return original input if not a valid number
  }

  // Handle numbers less than 1000
  if (num < 1000) {
    return num.toFixed(decimals);
  }

  // Define the suffixes and their corresponding scales
  const suffixes = [
    { value: 1e3, symbol: "k" }, // Thousands
    { value: 1e6, symbol: "m" }, // Millions
    { value: 1e9, symbol: "B" }, // Billions
    { value: 1e12, symbol: "T" }, // Trillions
  ];

  // Find the appropriate suffix
  let i;
  for (i = suffixes.length - 1; i > 0; i--) {
    if (num >= suffixes[i].value) {
      break;
    }
  }

  // Calculate the scaled number and format it
  const scaledNum = num / suffixes[i].value;

  // Use toFixed to control decimal places, but remove trailing zeros if the number is round
  const formattedNum = parseFloat(scaledNum.toFixed(decimals));

  return formattedNum + suffixes[i].symbol;
};

// Example Usage:
// logger.info(formatLargeNumber(11000));         // Output: "11k"
// logger.info(formatLargeNumber(11500));         // Output: "11.5k"
// logger.info(formatLargeNumber(11000000));      // Output: "11m"
// logger.info(formatLargeNumber(11250000));      // Output: "11.3m" (with default 1 decimal)
// logger.info(formatLargeNumber(11000000000));   // Output: "11B"
// logger.info(formatLargeNumber(11300000000));   // Output: "11.3B"
// logger.info(formatLargeNumber(11000000000000)); // Output: "11T"
// logger.info(formatLargeNumber(11700000000000)); // Output: "11.7T"
// logger.info(formatLargeNumber(500));           // Output: "500"
// logger.info(formatLargeNumber(1234567, 2));    // Output: "1.23m" (with 2 decimals)
// logger.info(formatLargeNumber(999));           // Output: "999"
// logger.info(formatLargeNumber(1000));          // Output: "1k"
